import Header from '@/react-app/components/Header';
import Footer from '@/react-app/components/Footer';
import { Shield, Lock, Eye, CheckCircle, AlertTriangle, Key, Server, Globe, Mail, Phone } from 'lucide-react';

export default function Security() {
    return (
        <div className="min-h-screen bg-white dark:bg-gray-900">
            <Header />

            {/* Hero Section */}
            <section className="pt-16 bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                    <div className="text-center mb-12">
                        <div className="bg-blue-100 dark:bg-blue-900 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                            <Shield className="h-10 w-10 text-blue-600 dark:text-blue-400" />
                        </div>
                        <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6">
                            Security & Protection
                        </h1>
                        <p className="text-xl text-gray-600 dark:text-gray-300 leading-relaxed">
                            Your financial security is our top priority. Learn about our comprehensive security measures and best practices.
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                            Last updated: September 27, 2025
                        </p>
                    </div>
                </div>
            </section>

            {/* Content */}
            <section className="py-16">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

                    {/* Security Overview */}
                    <div className="mb-16">
                        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">Our Security Framework</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            <div className="text-center p-6 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                                <div className="bg-blue-100 dark:bg-blue-800 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                                    <Lock className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Data Encryption</h3>
                                <p className="text-gray-600 dark:text-gray-300">Bank-level 256-bit SSL encryption protects all data transmission and storage.</p>
                            </div>

                            <div className="text-center p-6 bg-green-50 dark:bg-green-900/20 rounded-xl">
                                <div className="bg-green-100 dark:bg-green-800 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                                    <Key className="h-8 w-8 text-green-600 dark:text-green-400" />
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Multi-Factor Auth</h3>
                                <p className="text-gray-600 dark:text-gray-300">Advanced authentication methods secure your account access.</p>
                            </div>

                            <div className="text-center p-6 bg-purple-50 dark:bg-purple-900/20 rounded-xl">
                                <div className="bg-purple-100 dark:bg-purple-800 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                                    <Eye className="h-8 w-8 text-purple-600 dark:text-purple-400" />
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">24/7 Monitoring</h3>
                                <p className="text-gray-600 dark:text-gray-300">Continuous monitoring detects and prevents suspicious activities.</p>
                            </div>
                        </div>
                    </div>

                    {/* Data Protection */}
                    <div className="mb-12">
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                            <Server className="h-6 w-6 text-blue-600 dark:text-blue-400 mr-3" />
                            Data Protection & Encryption
                        </h2>

                        <div className="space-y-6">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Data Encryption</h3>
                                    <ul className="space-y-3">
                                        <li className="flex items-start">
                                            <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                                            <span className="text-gray-600 dark:text-gray-300">256-bit AES encryption for data at rest</span>
                                        </li>
                                        <li className="flex items-start">
                                            <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                                            <span className="text-gray-600 dark:text-gray-300">TLS 1.3 encryption for data in transit</span>
                                        </li>
                                        <li className="flex items-start">
                                            <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                                            <span className="text-gray-600 dark:text-gray-300">End-to-end encryption for sensitive communications</span>
                                        </li>
                                        <li className="flex items-start">
                                            <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                                            <span className="text-gray-600 dark:text-gray-300">Hardware Security Modules (HSM) for key management</span>
                                        </li>
                                    </ul>
                                </div>

                                <div>
                                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Data Storage</h3>
                                    <ul className="space-y-3">
                                        <li className="flex items-start">
                                            <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                                            <span className="text-gray-600 dark:text-gray-300">Secure cloud infrastructure with ISO 27001 certification</span>
                                        </li>
                                        <li className="flex items-start">
                                            <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                                            <span className="text-gray-600 dark:text-gray-300">Geographic data redundancy and backup</span>
                                        </li>
                                        <li className="flex items-start">
                                            <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                                            <span className="text-gray-600 dark:text-gray-300">Regular data integrity monitoring</span>
                                        </li>
                                        <li className="flex items-start">
                                            <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                                            <span className="text-gray-600 dark:text-gray-300">Automated backup and disaster recovery</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Authentication & Access Control */}
                    <div className="mb-12">
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                            <Key className="h-6 w-6 text-blue-600 dark:text-blue-400 mr-3" />
                            Authentication & Access Control
                        </h2>

                        <div className="space-y-6">
                            <div>
                                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Multi-Factor Authentication (MFA)</h3>
                                <p className="text-gray-600 dark:text-gray-300 mb-4">
                                    We require multi-factor authentication for all account access to ensure only you can access your financial data.
                                </p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="border border-gray-200 dark:border-gray-700 p-4 rounded-lg">
                                        <h4 className="font-semibold text-gray-900 dark:text-white mb-2">SMS Verification</h4>
                                        <p className="text-gray-600 dark:text-gray-300 text-sm">Secure codes sent to your registered mobile number</p>
                                    </div>
                                    <div className="border border-gray-200 dark:border-gray-700 p-4 rounded-lg">
                                        <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Email Verification</h4>
                                        <p className="text-gray-600 dark:text-gray-300 text-sm">Verification links sent to your registered email</p>
                                    </div>
                                    <div className="border border-gray-200 dark:border-gray-700 p-4 rounded-lg">
                                        <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Authenticator Apps</h4>
                                        <p className="text-gray-600 dark:text-gray-300 text-sm">Support for Google Authenticator and similar apps</p>
                                    </div>
                                    <div className="border border-gray-200 dark:border-gray-700 p-4 rounded-lg">
                                        <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Biometric Login</h4>
                                        <p className="text-gray-600 dark:text-gray-300 text-sm">Fingerprint and face recognition on mobile devices</p>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Access Controls</h3>
                                <ul className="space-y-3">
                                    <li className="flex items-start">
                                        <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                                        <span className="text-gray-600 dark:text-gray-300">Session timeout and automatic logout</span>
                                    </li>
                                    <li className="flex items-start">
                                        <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                                        <span className="text-gray-600 dark:text-gray-300">Device recognition and trusted device management</span>
                                    </li>
                                    <li className="flex items-start">
                                        <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                                        <span className="text-gray-600 dark:text-gray-300">Login attempt monitoring and account lockout protection</span>
                                    </li>
                                    <li className="flex items-start">
                                        <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                                        <span className="text-gray-600 dark:text-gray-300">Geographic access restrictions and suspicious activity alerts</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Fraud Prevention */}
                    <div className="mb-12">
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                            <Eye className="h-6 w-6 text-blue-600 dark:text-blue-400 mr-3" />
                            Fraud Prevention & Monitoring
                        </h2>

                        <div className="space-y-6">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Real-Time Monitoring</h3>
                                    <ul className="space-y-3">
                                        <li className="flex items-start">
                                            <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                                            <span className="text-gray-600 dark:text-gray-300">24/7 automated transaction monitoring</span>
                                        </li>
                                        <li className="flex items-start">
                                            <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                                            <span className="text-gray-600 dark:text-gray-300">AI-powered fraud detection algorithms</span>
                                        </li>
                                        <li className="flex items-start">
                                            <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                                            <span className="text-gray-600 dark:text-gray-300">Behavioral analysis and anomaly detection</span>
                                        </li>
                                        <li className="flex items-start">
                                            <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                                            <span className="text-gray-600 dark:text-gray-300">Instant alerts for suspicious activities</span>
                                        </li>
                                    </ul>
                                </div>

                                <div>
                                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Transaction Security</h3>
                                    <ul className="space-y-3">
                                        <li className="flex items-start">
                                            <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                                            <span className="text-gray-600 dark:text-gray-300">Transaction limits and approval workflows</span>
                                        </li>
                                        <li className="flex items-start">
                                            <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                                            <span className="text-gray-600 dark:text-gray-300">Multi-signature requirements for large transactions</span>
                                        </li>
                                        <li className="flex items-start">
                                            <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                                            <span className="text-gray-600 dark:text-gray-300">Withdrawal cooling-off periods</span>
                                        </li>
                                        <li className="flex items-start">
                                            <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                                            <span className="text-gray-600 dark:text-gray-300">Advanced identity verification for sensitive operations</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Compliance & Regulations */}
                    <div className="mb-12">
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                            <Globe className="h-6 w-6 text-blue-600 dark:text-blue-400 mr-3" />
                            Compliance & Regulatory Standards
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div>
                                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Financial Regulations</h3>
                                <ul className="space-y-3">
                                    <li className="flex items-start">
                                        <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                                        <span className="text-gray-600 dark:text-gray-300">Anti-Money Laundering (AML) compliance</span>
                                    </li>
                                    <li className="flex items-start">
                                        <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                                        <span className="text-gray-600 dark:text-gray-300">Know Your Customer (KYC) verification</span>
                                    </li>
                                    <li className="flex items-start">
                                        <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                                        <span className="text-gray-600 dark:text-gray-300">PCI DSS compliance for payment processing</span>
                                    </li>
                                    <li className="flex items-start">
                                        <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                                        <span className="text-gray-600 dark:text-gray-300">SOX compliance for financial reporting</span>
                                    </li>
                                </ul>
                            </div>

                            <div>
                                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Data Protection</h3>
                                <ul className="space-y-3">
                                    <li className="flex items-start">
                                        <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                                        <span className="text-gray-600 dark:text-gray-300">GDPR compliance for EU users</span>
                                    </li>
                                    <li className="flex items-start">
                                        <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                                        <span className="text-gray-600 dark:text-gray-300">CCPA compliance for California users</span>
                                    </li>
                                    <li className="flex items-start">
                                        <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                                        <span className="text-gray-600 dark:text-gray-300">ISO 27001 information security standards</span>
                                    </li>
                                    <li className="flex items-start">
                                        <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                                        <span className="text-gray-600 dark:text-gray-300">Regular security audits and penetration testing</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Security Best Practices */}
                    <div className="mb-12">
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Security Best Practices for Users</h2>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <div className="bg-green-50 dark:bg-green-900/20 p-6 rounded-xl">
                                <h3 className="text-xl font-semibold text-green-800 dark:text-green-200 mb-4 flex items-center">
                                    <CheckCircle className="h-6 w-6 mr-2" />
                                    Do's
                                </h3>
                                <ul className="space-y-2 text-green-700 dark:text-green-300">
                                    <li>• Use strong, unique passwords</li>
                                    <li>• Enable two-factor authentication</li>
                                    <li>• Log out from shared computers</li>
                                    <li>• Keep your contact information updated</li>
                                    <li>• Monitor your account regularly</li>
                                    <li>• Use secure internet connections</li>
                                    <li>• Report suspicious activities immediately</li>
                                </ul>
                            </div>

                            <div className="bg-red-50 dark:bg-red-900/20 p-6 rounded-xl">
                                <h3 className="text-xl font-semibold text-red-800 dark:text-red-200 mb-4 flex items-center">
                                    <AlertTriangle className="h-6 w-6 mr-2" />
                                    Don'ts
                                </h3>
                                <ul className="space-y-2 text-red-700 dark:text-red-300">
                                    <li>• Never share your login credentials</li>
                                    <li>• Don't use public Wi-Fi for transactions</li>
                                    <li>• Don't click suspicious email links</li>
                                    <li>• Never provide information to unsolicited callers</li>
                                    <li>• Don't save passwords in browsers on shared devices</li>
                                    <li>• Avoid using obvious security questions</li>
                                    <li>• Don't ignore security alerts</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Incident Response */}
                    <div className="mb-12">
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Incident Response & Recovery</h2>

                        <div className="space-y-6">
                            <p className="text-gray-600 dark:text-gray-300">
                                We maintain a comprehensive incident response plan to quickly address any security threats or breaches.
                            </p>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div className="text-center p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400 mb-2">&lt;15 min</div>
                                    <p className="text-gray-600 dark:text-gray-300 text-sm">Incident Detection Time</p>
                                </div>
                                <div className="text-center p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400 mb-2">&lt;1 hour</div>
                                    <p className="text-gray-600 dark:text-gray-300 text-sm">Response Team Activation</p>
                                </div>
                                <div className="text-center p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400 mb-2">24/7</div>
                                    <p className="text-gray-600 dark:text-gray-300 text-sm">Security Operations Center</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Contact Information */}
                    <div className="mb-12">
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                            <Mail className="h-6 w-6 text-blue-600 dark:text-blue-400 mr-3" />
                            Security Support
                        </h2>
                        <div className="bg-blue-50 dark:bg-blue-900/20 p-6 rounded-lg">
                            <p className="text-gray-600 dark:text-gray-300 mb-4">
                                If you have any questions about these security measures, please contact us:
                            </p>
                            <div className="space-y-2 text-gray-600 dark:text-gray-300">
                                <div className="flex items-center">
                                    <Mail className="h-4 w-4 text-blue-600 dark:text-blue-400 mr-2" />
                                    <span>Email: support@tradeenrich.com</span>
                                </div>
                                <div className="flex items-center">
                                    <Phone className="h-4 w-4 text-blue-600 dark:text-blue-400 mr-2" />
                                    <span>Phone: +234 7063441554</span>
                                </div>
                                <div className="mt-4">
                                    <p className="font-medium">Mailing Address:</p>
                                    <p>Trade Enrich Privacy Team<br />
                                        Thomas and Paulina Ibe's New Compound by Nkwo Mbaise Market Umuodo Itu Autonomous Community Ezinihitte-Mbaise LGA, Imo State, Nigeria<br />
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </section>

            <Footer />
        </div>
    );
}
